import React, { useMemo, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Col, Container, Row } from "react-bootstrap";
import BulkUpload from "./BulkUpload";
import FilterModal from "./FilterModal";
import SampleBulkUploadFile from "./SampleBulkUploadFile";
import {
  useGetUsersListFilterQuery,
  useLazyExportFilterUsersListQuery,
} from "../../../../../../services/user-service";
import CreateUserModal from "./CreateUserModal";
import PageLoader from "../../../../../components/PageLoader";
import SharedPagination from "../../../../../components/Pagination";
import UsersTableComponent from "../data/UsersTable";
import UserModal from "./UserModal";
import EditUserModal from "./EditUserModal";
import DeleteUser from "./DeleteUser";

const UsersDataTable = ({ searchQuery }) => {
  const roles = sessionStorage.getItem("roles");
  const [selectedUser, setSelectedUser] = useState(null);
  const [openViewUser, setOpenViewUser] = useState(false);
  const [openEditUser, setOpenEditUser] = useState(false);
  const [openDeleteUser, setOpenDeleteUser] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageSize] = useState(100);
  const [filterOptions, setFilterOptions] = useState({
    age: "",
    gender: "",
    registration_channel: "",
    start_date: "",
    end_date: "",
    page_index: 0,
    size: 100,
    sort: "DESC",
  });

  const handleFilter = (options) => {
    setFilterOptions((prev) => ({
      ...prev,
      ...options,
    }));
    setCurrentPageIndex(0);
  };

  const fetchParams = useMemo(
    () => ({
      page_index: currentPageIndex,
      size: pageSize,
      ...(searchQuery ? { search: searchQuery } : {}),
      ...Object.fromEntries(
        Object.entries(filterOptions).filter(([_, value]) => value)
      ),
    }),
    [currentPageIndex, pageSize, searchQuery, filterOptions]
  );

  const { data: users, isLoading } = useGetUsersListFilterQuery(fetchParams);

  const handleResetPage = () => {
    setCurrentPageIndex(0);
  };

  const handleViewUser = (userId, userData) => {
    setSelectedUser({ userId, userData });
    setOpenViewUser(true);
  };

  const handleEditUser = (userId, userData) => {
    setSelectedUser({ userId, userData });
    setOpenEditUser(true);
  };

  const handleDeleteUser = (userId) => {
    setSelectedUser(userId);
    setOpenDeleteUser(true);
  };

  const shouldDisplayData = roles !== "Coordinator" || searchQuery;
  const totalPages = users?.totalPages || 0;
  const totalElements = users?.totalElements || 0;

  const [exportUsers] = useLazyExportFilterUsersListQuery();

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const response = await exportUsers(fetchParams);
      if (response.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "users.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container>
      <Row>
        <Col>
          <CreateUserModal />
        </Col>
        <Col className="d-flex flex-wrap justify-content-end align-items-center mb-4">
          <SampleBulkUploadFile />
          <FilterModal
            onFilter={handleFilter}
            resetPage={handleResetPage}
            onExport={handleExport}
            isExporting={isExporting}
          />
          <BulkUpload />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="w-100 h-auto">
            {isLoading ? (
              <PageLoader />
            ) : (
              <>
                {shouldDisplayData ? (
                  <>
                    <DataGrid
                      rows={(users?.data || []) as any[]}
                      columns={UsersTableComponent(
                        handleViewUser,
                        handleEditUser,
                        handleDeleteUser
                      )}
                      checkboxSelection
                      disableColumnFilter
                      disableColumnSelector
                      disableDensitySelector
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: false,
                        },
                      }}
                      sx={{
                        "& .header--bg": {
                          backgroundColor: "#EBF1FC",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#EBF1FC",
                          fontSize: "12px",
                        },
                        "& .Mui-checked .MuiSvgIcon-root": {
                          fill: "black",
                          background: "white",
                        },
                        "& .MuiSvgIcon-root": {
                          width: "18px",
                          height: "18px",
                          borderRadius: "4px",
                          color: "#EBEBEB",
                        },
                        "&.MuiDataGrid-root": {
                          border: "none",
                          fontSize: "14px",
                          fontWeight: "400",
                          fontFamily: "Inter",
                          backgroundColor: "white",
                        },

                        "& .MuiDataGrid-footerContainer": {
                          display: "none",
                        },
                      }}
                    />
                    <SharedPagination
                      currentPage={currentPageIndex + 1}
                      onPageChange={(newPage) =>
                        setCurrentPageIndex(newPage - 1)
                      }
                      totalPages={totalPages}
                      totalElements={totalElements}
                      pageSize={pageSize}
                    />
                  </>
                ) : (
                  <p>No data available.</p>
                )}
              </>
            )}
          </div>
        </Col>
      </Row>
      {selectedUser && (
        <>
          <UserModal
            userId={selectedUser.userId}
            userData={selectedUser.userData}
            show={openViewUser}
            onClose={() => setOpenViewUser(false)}
          />
          <EditUserModal
            userId={selectedUser.userId}
            userData={selectedUser.userData}
            show={openEditUser}
            onClose={() => setOpenEditUser(false)}
          />
          <DeleteUser
            user={selectedUser}
            show={openDeleteUser}
            onClose={() => setOpenDeleteUser(false)}
          />
        </>
      )}
    </Container>
  );
};

export default UsersDataTable;
